import { useEffect, useState } from 'react';
import CampoImporto from '../components/CampoImporto';
import { Link } from 'react-router-dom';
import { offerteConsulentiApi, consulentiApi, commesseApi } from '../api';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import ProgressBar from '../components/ProgressBar';
import SelettoreCommessa from '../components/SelettoreCommessa';
import AllegatoOfferta from '../components/AllegatoOfferta';
import IncaricoConsulente from '../components/IncaricoConsulente';
import CampoImpegno from '../components/CampoImpegno';
import { etichettaImpegnoConsulente } from '../components/impegno';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const STATI = ['da_approvare', 'attiva', 'rifiutata', 'completata', 'chiusa', 'annullata'];
const STATO_LABEL = {
  da_approvare: 'Da approvare', attiva: 'Attiva', rifiutata: 'Rifiutata',
  completata: 'Completata', chiusa: 'Chiusa', annullata: 'Annullata',
};
const STATO_STILE = {
  da_approvare: 'bg-blue-100 text-blue-700', attiva: 'bg-green-100 text-green-700',
  rifiutata: 'bg-red-100 text-red-600', completata: 'bg-gray-100 text-gray-600',
  chiusa: 'bg-gray-100 text-gray-600', annullata: 'bg-gray-100 text-gray-500',
};
const empty = { consulente_id: '', commessa_id: '', numero: '', descrizione: '', importo_max: '', ore_previste: '', unita_previste: 'giorno', data_offerta: '', data_scadenza: '', stato: 'attiva', note: '', modalita_esecuzione: 'corpo' };

export default function OfferteConsulenti() {
  const [rows, setRows] = useState([]);
  const [consulenti, setConsulenti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [filtroStato, setFiltroStato] = useState('');
  // Ricerca libera: quando non si ricorda il numero si cerca per tema, cliente
  // o consulente, ed è così che si pensa a un'offerta.
  const [cerca, setCerca] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [allegato, setAllegato] = useState(null);   // file scelto, caricato dopo il salvataggio

  const load = () => offerteConsulentiApi.list({ stato: filtroStato || undefined }).then(setRows);
  useEffect(() => { load(); }, [filtroStato]);
  useEffect(() => { consulentiApi.list().then(setConsulenti); commesseApi.list().then(setCommesse); }, []);

  const openNew = () => { setForm(empty); setAllegato(null); setModal({ mode: 'new' }); };
  const openEdit = (r) => {
    setForm({ ...r, data_offerta: r.data_offerta?.slice(0,10)||'', data_scadenza: r.data_scadenza?.slice(0,10)||'' });
    setAllegato(null);
    setModal({ mode: 'edit', id: r.id });
  };

  const save = async () => {
    setSaving(true);
    try {
      const salvata = modal.mode === 'new'
        ? await offerteConsulentiApi.create(form)
        : await offerteConsulentiApi.update(modal.id, form);
      // L'allegato si carica dopo: in creazione l'id dell'offerta nasce ora
      const id = modal.mode === 'new' ? salvata?.id : modal.id;
      if (allegato && id) await offerteConsulentiApi.caricaAllegato(id, allegato);
      setModal(null); setAllegato(null); load();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm('Eliminare?')) return;
    await offerteConsulentiApi.delete(id); load();
  };

  const approva = async (r) => {
    const note = prompt(`Approvare l'offerta ${r.numero || ''} di ${r.consulente_nome || ''} ${r.consulente_cognome || ''}?\nNote (facoltative):`, '');
    if (note === null) return;
    try { await offerteConsulentiApi.approva(r.id, { note_admin: note || null }); load(); }
    catch (e) { alert(e); }
  };

  const rifiuta = async (r) => {
    const note = prompt('Motivazione del rifiuto (obbligatoria):', '');
    if (note === null) return;
    if (!note.trim()) return alert('La motivazione è obbligatoria');
    try { await offerteConsulentiApi.rifiuta(r.id, { note_admin: note }); load(); }
    catch (e) { alert(e); }
  };

  const daApprovare = rows.filter(r => r.stato === 'da_approvare').length;

  // Si cerca in tutto ciò che descrive l'offerta: il tema sta nella
  // descrizione o nel titolo della commessa, non nel numero.
  const t = cerca.trim().toLowerCase();
  const visibili = !t ? rows : rows.filter(r =>
    [r.consulente_nome, r.consulente_cognome, r.numero, r.descrizione, r.note,
     r.commessa_cliente, r.offerta_cliente, r.commessa_titolo, r.offerta_titolo,
     r.commessa_numero, r.offerta_numero]
      .some(v => String(v || '').toLowerCase().includes(t)));

  // Una società non manda un'offerta: le si emette un ordine, e il numero lo
  // diamo noi. Per le persone il numero resta il loro, come è sempre stato.
  const societaScelta = consulenti.some(
    c => String(c.id) === String(form.consulente_id) && c.tipo === 'societa');

  const proponiNumeroOda = async (forza) => {
    if (!forza && String(form.numero || '').trim()) return;
    try {
      const anno = new Date(form.data_offerta || Date.now()).getFullYear();
      const d = await offerteConsulentiApi.nextNumeroOda(anno);
      setForm(p => ({ ...p, numero: d.numero }));
    } catch { /* il numero resta a mano */ }
  };

  const scegliControparte = (id) => {
    const c = consulenti.find(x => String(x.id) === String(id));
    setForm(p => ({ ...p, consulente_id: id }));
    if (c?.tipo === 'societa' && !String(form.numero || '').trim()) {
      const anno = new Date(form.data_offerta || Date.now()).getFullYear();
      offerteConsulentiApi.nextNumeroOda(anno)
        .then(d => setForm(p => (String(p.numero || '').trim() ? p : { ...p, numero: d.numero })))
        .catch(() => {});
    }
  };

  const f = (k) => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title flex items-center gap-2">
          Offerte consulenti
          {daApprovare > 0 && (
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{daApprovare} da approvare</span>
          )}
        </h1>
        <button className="btn-primary" onClick={openNew}>+ Nuova offerta</button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {['', ...STATI].map(s => (
          <button key={s} onClick={() => setFiltroStato(s)}
            className={`btn-sm btn ${filtroStato === s ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
            {s ? STATO_LABEL[s] : 'Tutte'}
          </button>
        ))}
        {/* La larghezza sta sul contenitore: .input impone w-full */}
        <div className="w-80 ml-auto">
          <input className="input py-1 text-sm" type="search"
                 placeholder="Cerca per tema, cliente, consulente, numero…"
                 value={cerca} onChange={e => setCerca(e.target.value)} />
        </div>
      </div>
      {cerca.trim() && (
        <p className="text-xs text-gray-500 -mt-2">
          {visibili.length} di {rows.length} offerte corrispondono a "{cerca.trim()}".
        </p>
      )}

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 text-left">
              <th className="px-4 py-3">Consulente</th>
              <th className="px-4 py-3">Cliente e riferimento</th>
              <th className="px-4 py-3">Numero</th>
              <th className="px-4 py-3">Importo max</th>
              <th className="px-4 py-3">Ore prev.</th>
              <th className="px-4 py-3">Imputato</th>
              <th className="px-4 py-3 w-36">Avanzamento</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visibili.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                {t ? `Nessuna offerta corrisponde a "${cerca.trim()}"` : 'Nessuna offerta consulente'}
              </td></tr>
            )}
            {visibili.map(r => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{r.consulente_nome} {r.consulente_cognome}</td>
                {/* Il solo numero non dice a cosa si riferisce l'offerta: servono
                    il cliente e una riga di descrizione per riconoscerla a colpo
                    d'occhio senza aprire la commessa. */}
                <td className="px-4 py-3 max-w-[300px]">
                  {(() => {
                    const cliente = r.commessa_cliente || r.offerta_cliente;
                    const titolo = r.commessa_titolo || r.offerta_titolo;
                    const numero = r.commessa_numero || r.offerta_numero;
                    if (!cliente && !titolo && !numero) return <span className="text-gray-300">—</span>;
                    return (
                      <>
                        {cliente && <div className="font-medium text-gray-800 truncate">{cliente}</div>}
                        {titolo && (
                          <div className="text-xs text-gray-600 truncate" title={titolo}>{titolo}</div>
                        )}
                        <div className="flex gap-2 items-baseline mt-0.5">
                          {r.commessa_id ? (
                            <Link to={`/commesse/${r.commessa_id}`}
                                  className="font-mono text-[11px] text-brand-600 hover:underline">
                              {r.commessa_numero}
                            </Link>
                          ) : r.offerta_numero ? (
                            <span className="font-mono text-[11px] text-brand-600">{r.offerta_numero}</span>
                          ) : null}
                          {/* Quando ci sono entrambi si mostra anche l'offerta di origine */}
                          {r.commessa_id && r.offerta_numero && (
                            <span className="font-mono text-[11px] text-gray-400">
                              da {r.offerta_numero}
                            </span>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 font-mono text-gray-500">{r.numero || '—'}</td>
                <td className="px-4 py-3 font-medium">{fmt(r.importo_max)}</td>
                <td className="px-4 py-3 text-gray-600">
                  {r.modalita_esecuzione === 'economia'
                    ? (etichettaImpegnoConsulente(r.ore_previste, r.unita_previste) || '—')
                    : <span className="text-xs text-gray-400">a corpo</span>}
                </td>
                <td className="px-4 py-3 text-red-600">{fmt(r.importo_imputato)}</td>
                <td className="px-4 py-3"><ProgressBar value={r.importo_imputato} max={r.importo_max} /></td>
                <td className="px-4 py-3 text-gray-500">{fmtDate(r.data_offerta)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATO_STILE[r.stato] || 'bg-gray-100'}`}>
                    {STATO_LABEL[r.stato] || r.stato}
                  </span>
                  {r.note_admin && <div className="text-xs text-gray-400 mt-0.5 max-w-[160px] truncate">💬 {r.note_admin}</div>}
                </td>
                {/* La cella resta una cella di tabella: renderla "flex" le
                    faceva perdere l'allineamento con la riga, e con i pulsanti
                    dell'incarico andava a capo a caso. I pulsanti stanno in
                    gruppi incolonnati a destra, uno per tipo di azione. */}
                <td className="px-4 py-3 align-middle">
                  <div className="flex flex-col items-end gap-1.5">
                    {r.stato === 'da_approvare' && (
                      <div className="flex gap-1">
                        <button className="btn-sm btn bg-green-100 text-green-700 hover:bg-green-200 font-semibold whitespace-nowrap"
                                onClick={() => approva(r)}>✓ Approva</button>
                        <button className="btn-sm btn bg-red-100 text-red-600 hover:bg-red-200"
                                title="Rifiuta" onClick={() => rifiuta(r)}>✕</button>
                      </div>
                    )}
                    <IncaricoConsulente offerta={r} onCambiato={load} />
                    <div className="flex gap-1">
                      <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}>Modifica</button>
                      <button className="btn-danger btn-sm" onClick={() => del(r.id)}>Elimina</button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Nuova offerta consulente' : 'Modifica offerta consulente'} onClose={() => setModal(null)} size="lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{societaScelta ? 'Società *' : 'Consulente / società *'}</label>
              <select className="input" value={form.consulente_id || ''}
                      onChange={e => scegliControparte(e.target.value)}>
                <option value="">— Seleziona —</option>
                <optgroup label="Consulenti">
                  {consulenti.filter(c => c.tipo !== 'societa')
                    .map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
                </optgroup>
                <optgroup label="Società">
                  {consulenti.filter(c => c.tipo === 'societa')
                    .map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </optgroup>
              </select>
            </div>
            <div>
              <label className="label">
                Commessa *
                <span className="ml-1 text-xs text-gray-400 font-normal">(cercabile per cliente)</span>
              </label>
              <SelettoreCommessa
                commesse={commesse}
                value={form.commessa_id}
                onChange={v => setForm(p => ({ ...p, commessa_id: v }))}
                placeholder="— Seleziona la commessa —"
              />
            </div>
            <div>
              <label className="label">{societaScelta ? 'Numero ordine (ODA)' : 'Numero offerta'}</label>
              <div className="flex gap-1">
                <input className="input font-mono" {...f('numero')} />
                {/* Alle società il numero lo diamo noi: il progressivo si
                    ricalcola a richiesta, se è stato corretto a mano. */}
                {societaScelta && (
                  <button type="button" className="btn-secondary shrink-0" title="Proponi il prossimo numero"
                          onClick={() => proponiNumeroOda(true)}>↻</button>
                )}
              </div>
              {societaScelta && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Progressivo nostro: ODA + numero + anno. Si può correggere.
                </p>
              )}
            </div>
            <div>
              <label className="label">Stato</label>
              <select className="input" value={form.stato} onChange={e => setForm(p => ({ ...p, stato: e.target.value }))}>
                {STATI.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Modalità</label>
              <select className="input" {...f('modalita_esecuzione')}>
                <option value="corpo">A corpo (importo fisso)</option>
                <option value="economia">In economia (a giornate/ore)</option>
              </select>
            </div>
            <div>
              <label className="label">
                {form.modalita_esecuzione === 'economia' ? 'Tetto di spesa (€)' : 'Importo concordato (€)'}
              </label>
              <CampoImporto className="input" value={form.importo_max} onChange={v => setForm(p => ({ ...p, importo_max: v }))} />
            </div>
            {/* Le ore hanno senso solo in economia: a corpo il compenso non
                dipende dal tempo impiegato, e chiederle produceva il campo a
                zero che si vede su tutte le offerte esistenti. */}
            {form.modalita_esecuzione === 'economia' && (
              <div>
                <label className="label">Impegno previsto</label>
                <CampoImpegno ore={form.ore_previste} unita={form.unita_previste}
                              onChange={({ ore, unita }) => setForm(p => ({ ...p, ore_previste: ore, unita_previste: unita }))} />
                <p className="text-xs text-gray-400 mt-0.5">
                  Usato per il controllo di sforamento insieme al tetto di spesa, e scritto
                  così nella lettera d'incarico.
                </p>
              </div>
            )}
            <div><label className="label">Data offerta</label><input className="input" type="date" {...f('data_offerta')} /></div>
            <div><label className="label">Data scadenza</label><input className="input" type="date" {...f('data_scadenza')} /></div>
            <div className="col-span-2">
              <AllegatoOfferta
                offertaId={modal.mode === 'edit' ? modal.id : null}
                file={allegato}
                onFile={setAllegato}
                onCampiLetti={(c) => setForm(p => ({
                  ...p,
                  // Non si sovrascrive ciò che è già stato scritto a mano
                  numero: p.numero || c.numero || '',
                  descrizione: p.descrizione || c.descrizione || '',
                  importo_max: p.importo_max || (c.importo_max ?? ''),
                  ore_previste: p.ore_previste || (c.ore_previste ?? ''),
                  data_offerta: p.data_offerta || c.data_offerta || '',
                  data_scadenza: p.data_scadenza || c.data_scadenza || '',
                  note: p.note || c.note || '',
                }))}
              />
            </div>
            <div className="col-span-2"><label className="label">Descrizione</label><textarea className="input" rows={2} {...f('descrizione')} /></div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={2} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
